import { useEffect, useState, type FormEvent } from "react";
import { api, type Funnel, type FunnelStep, type FunnelResult } from "../api";
import { useProjects } from "../project-context";

const RANGES = ["24h", "7d", "30d", "90d"] as const;

function FunnelView({ funnel, range, onDelete }: { funnel: Funnel; range: string; onDelete: () => void }) {
  const [res, setRes] = useState<FunnelResult | null>(null);
  useEffect(() => {
    api.funnelResults(funnel.id, range).then(setRes).catch(() => setRes(null));
  }, [funnel.id, range]);

  const max = res ? Math.max(1, ...res.steps.map((s) => s.count)) : 1;

  return (
    <div className="card reveal" style={{ marginBottom: 16 }}>
      <h3 className="card-title">
        {funnel.name}
        <button className="copy" onClick={onDelete}>slett</button>
      </h3>
      {!res ? (
        <div className="empty">beregner …</div>
      ) : (
        res.steps.map((s, i) => (
          <div className="funnel-step" key={i}>
            <div className="top">
              <span className="lbl">
                {s.label}
                <small>{s.kind === "event" ? `event:${s.value}` : s.value}</small>
              </span>
              <span className="cnt">
                {new Intl.NumberFormat("nb-NO").format(s.count)}
                <span className="pct">{s.pctOfStart}% av start</span>
              </span>
            </div>
            <div className="funnel-bar">
              <div className="funnel-fill" style={{ width: `${(s.count / max) * 100}%` }} />
            </div>
            {i > 0 && s.pctOfPrev < 100 && (
              <div className="funnel-drop">−{(100 - s.pctOfPrev).toFixed(1)}% fra forrige steg</div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

const EMPTY_STEP: FunnelStep = { kind: "path", value: "" };

export function Funnels() {
  const { selected, loading: projLoading } = useProjects();
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [range, setRange] = useState("7d");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [steps, setSteps] = useState<FunnelStep[]>([{ ...EMPTY_STEP }, { ...EMPTY_STEP }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reload() {
    if (!selected) return;
    api.funnels(selected.id).then(setFunnels).catch(() => setFunnels([]));
  }
  useEffect(reload, [selected]);

  function updateStep(i: number, patch: Partial<FunnelStep>) {
    setSteps((s) => s.map((st, j) => (j === i ? { ...st, ...patch } : st)));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setErr(null);
    const clean = steps.filter((s) => s.value.trim());
    if (!name.trim() || clean.length < 2) {
      setErr("Navn og minst 2 utfylte steg kreves");
      return;
    }
    setBusy(true);
    try {
      await api.createFunnel(selected.id, { name: name.trim(), steps: clean });
      setName("");
      setSteps([{ ...EMPTY_STEP }, { ...EMPTY_STEP }]);
      setCreating(false);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunne ikke lagre");
    } finally {
      setBusy(false);
    }
  }

  if (projLoading) return <div className="spin" />;
  if (!selected) return <div className="card reveal" style={{ marginTop: 40 }}><div className="empty">ingen prosjekt valgt</div></div>;

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">{selected.name}</span>
          <h1 className="page-title">Funnels</h1>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="segment">
            {RANGES.map((r) => (
              <button key={r} className={r === range ? "on" : ""} onClick={() => setRange(r)}>{r}</button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? "Avbryt" : "Ny funnel"}
          </button>
        </div>
      </div>

      {creating && (
        <div className="card reveal" style={{ marginBottom: 16 }}>
          <h3 className="card-title">Definer funnel</h3>
          {err && <div className="error-msg">{err}</div>}
          <form onSubmit={save}>
            <div className="field">
              <label>Navn</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kjøpstrakt" required />
            </div>
            <label className="field" style={{ marginBottom: 8 }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)" }}>Steg (i rekkefølge)</span>
            </label>
            {steps.map((s, i) => (
              <div className="step-edit" key={i}>
                <select className="select" value={s.kind} onChange={(e) => updateStep(i, { kind: e.target.value as FunnelStep["kind"] })}>
                  <option value="path">Sidevisning</option>
                  <option value="event">Event</option>
                </select>
                <input
                  className="input"
                  value={s.value}
                  onChange={(e) => updateStep(i, { value: e.target.value })}
                  placeholder={s.kind === "path" ? "/pris" : "signup"}
                />
                <input
                  className="input"
                  style={{ maxWidth: 150 }}
                  value={s.label ?? ""}
                  onChange={(e) => updateStep(i, { label: e.target.value })}
                  placeholder="etikett (valgfri)"
                />
                {steps.length > 2 && (
                  <button type="button" className="x-btn" onClick={() => setSteps((st) => st.filter((_, j) => j !== i))}>✕</button>
                )}
              </div>
            ))}
            <div className="row" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-ghost" onClick={() => setSteps((s) => [...s, { ...EMPTY_STEP }])} disabled={steps.length >= 8}>
                + Legg til steg
              </button>
              <span className="spacer" />
              <button className="btn btn-primary" disabled={busy}>{busy ? "Lagrer …" : "Lagre funnel"}</button>
            </div>
          </form>
        </div>
      )}

      {funnels.length === 0 && !creating ? (
        <div className="card reveal"><div className="empty">ingen funnels ennå — lag din første</div></div>
      ) : (
        funnels.map((f) => (
          <FunnelView
            key={f.id}
            funnel={f}
            range={range}
            onDelete={async () => {
              if (!confirm(`Slette funnel «${f.name}»?`)) return;
              await api.deleteFunnel(f.id);
              reload();
            }}
          />
        ))
      )}
    </div>
  );
}
